import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createMonthlyBudgetSchema, CreateMonthlyBudgetInput } from "./monthlyBudgetSchema";
import { useCreateMonthlyBudget } from "@/hooks/useMonthlyBudgets";
import { useMasterBudgets, useMasterBudgetItems } from "@/hooks/useMasterBudgets";

// Generate YYYY-MM options for the next 24 months starting from current month
function generateMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleString("default", { month: "long", year: "numeric" });
    options.push({ value, label });
  }
  return options;
}

const MONTH_OPTIONS = generateMonthOptions();

function monthNameFromValue(monthValue: string): string {
  if (!monthValue) return "";
  const [year, month] = monthValue.split("-");
  const d = new Date(parseInt(year), parseInt(month) - 1, 1);
  return d.toLocaleString("default", { month: "long" });
}

interface CreateMonthlyBudgetDialogProps {
  trigger?: React.ReactNode;
}

export function CreateMonthlyBudgetDialog({ trigger }: CreateMonthlyBudgetDialogProps) {
  const [open, setOpen] = useState(false);
  const { mutateAsync, isPending } = useCreateMonthlyBudget();
  const { data: masterBudgets = [] } = useMasterBudgets();

  const form = useForm<CreateMonthlyBudgetInput>({
    resolver: zodResolver(createMonthlyBudgetSchema),
    defaultValues: {
      master_budget_id: "",
      title: "",
      description: "",
      month: MONTH_OPTIONS[0]?.value ?? "",
    },
  });

  const selectedMasterBudgetId = form.watch("master_budget_id");
  const selectedMonth = form.watch("month");

  // Fetch master budget items when a master budget is selected
  const { data: masterItems = [] } = useMasterBudgetItems(
    selectedMasterBudgetId || undefined
  );

  // Auto-update title when month changes
  useEffect(() => {
    if (selectedMonth) {
      const monthName = monthNameFromValue(selectedMonth);
      form.setValue("title", `${monthName} Budget`);
    }
  }, [selectedMonth, form]);

  const onSubmit = async (data: CreateMonthlyBudgetInput) => {
    const [yearStr, monthStr] = data.month.split("-");
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);

    await mutateAsync({
      master_budget_id: data.master_budget_id,
      title: data.title,
      description: data.description,
      year,
      month,
      items: masterItems.map((item) => ({
        category_id: item.category_id,
        sub_category_id: item.sub_category_id,
        amount: item.amount,
        currency: item.currency,
        description: item.description ?? undefined,
      })),
    });

    form.reset({
      master_budget_id: "",
      title: "",
      description: "",
      month: MONTH_OPTIONS[0]?.value ?? "",
    });
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? <Button>New Monthly Budget</Button>}
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Create Monthly Budget</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="master_budget_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Master Budget</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a master budget" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {masterBudgets.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name} ({b.year})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="month"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Month</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a month" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {MONTH_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., January Budget" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Brief description of this monthly budget"
                      className="resize-none"
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedMasterBudgetId && masterItems.length > 0 && (
              <p className="text-sm text-muted-foreground">
                {masterItems.length} item{masterItems.length !== 1 ? "s" : ""} will be pre-populated from the selected master budget.
              </p>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Creating..." : "Create"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
